import React, { useCallback, useEffect, useRef, useState } from "react";

// 在影像上點選與拖曳座標點。
//
// GRP、水尺線兩端、PIV 格網的三個角點都用這個元件 —— 叫人手打像素座標是
// 沒有人會用的介面，而舊程式就是把那些座標寫死在 main_D4.py / main_D5.py
// 裡，換一個測站就得改程式。
//
// 座標一律以**影像原始尺寸**回報，與顯示尺寸、縮放倍率無關：顯示大小會隨
// 視窗與縮放變動，但存進 config 的必須是影像本身的像素座標。
//
// 互動能力由「有沒有傳對應的 callback」決定，而不是一個 mode 字串。
// 第一版用 mode="add|move|none" 三選一，呼叫端全部傳了 "move"，於是
// `if (mode !== "add") return` 把新增整個關掉 —— 拖得動但點不出新的點，
// 而提示文字還寫著「點擊新增」。能力與 callback 綁在一起就不可能再對不上。
//
// 縮放：704×480 的畫面縮在 Modal 裡，一個像素不到一個螢幕點，消波塊的角、
// 石頭的邊根本點不準。所以：
//   * 滾輪以游標為中心縮放（1×–8×）
//   * 平移：按住右鍵或滾輪鍵拖曳（隨時可用），或在放大時用左鍵拖曳空白處
//   * 左鍵點擊 = 新增點
//   * 右下角的 − / 倍率 / + 按鈕，點倍率回到符合畫面
//
// 左鍵同時負責「點擊新增」與「拖曳平移」，兩者只能靠按下後移動的距離分辨。
// 第一版的門檻是 4 px：放大後小心對準、按下時手一抖超過 4 px，就被當成
// 平移 —— 影像只移動幾個像素，看起來像是點了沒反應。所以左鍵門檻放寬到
// 10 px，而且未放大時根本沒有東西可以平移，不再把點擊判成拖曳。需要精準
// 平移的人改用右鍵拖曳，與點擊完全不衝突。

const COLORS = {
  grp: "#ff3b30",
  line: "#ffcc00",
  grid: "#34c759",
  origin: "#007aff",
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
// 左鍵按下後移動超過這個距離（螢幕點）才算拖曳平移，否則視為點擊。手、
// 觸控板按壓都不可能完全不動，門檻太小就會吃掉點擊（見上方說明）
const LEFT_DRAG_THRESHOLD = 10;
// 右鍵 / 滾輪鍵只用來平移，不會和點擊衝突，稍微一動就開始跟手
const PAN_BUTTON_THRESHOLD = 2;

const FIT = { zoom: 1, ox: 0, oy: 0 };

const buttonStyle = {
  minWidth: 28, height: 24, padding: "0 6px", border: "none", borderRadius: 4,
  background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 12, cursor: "pointer",
  fontVariantNumeric: "tabular-nums",
};

export default function ImagePicker({
  src,
  points = [],            // [{x, y, label?, color?}]
  lines = [],             // [{from: {x,y}, to: {x,y}, color?, dashed?}]
  grid = [],              // [{x, y}] 只畫不可互動的衍生點（例如 PIV 格點）
  readOnly = false,
  maxPoints = Infinity,
  needPoints,             // 這一步需要幾個點，用來顯示進度
  onAdd, onMove, onRemove,
  height = 460,
  task,                   // 這個分頁要做的事，顯示在影像上方
}) {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  // 縮放倍率 1 時、影像以 contain 方式放進容器的位置與大小
  const [box, setBox] = useState({ width: 0, height: 0, left: 0, top: 0,
                                   outerWidth: 0 });
  // 縮放與平移：ox / oy 是相對於 box 位置的位移（螢幕點）
  const [view, setView] = useState(FIT);
  const [dragging, setDragging] = useState(null);
  const [panning, setPanning] = useState(false);
  const [cursor, setCursor] = useState(null);
  // 拖曳結束後瀏覽器仍會在容器上送出一次 click。沒有這個旗標的話，每次把
  // 點拖到新位置都會順便多新增一個點。
  const suppressClick = useRef(false);
  // 在空白處按下左鍵時記下起點；移動超過門檻就變成平移
  const panRef = useRef(null);

  // 滾輪事件掛在原生 listener 上（見下方），讀不到最新的 state，改從 ref 讀
  const boxRef = useRef(box);
  const viewRef = useRef(view);
  boxRef.current = box;
  viewRef.current = view;

  const canAdd = !readOnly && !!onAdd && points.length < maxPoints;
  const canMove = !readOnly && !!onMove;
  const canRemove = !readOnly && !!onRemove;

  // 限制平移範圍：影像比容器小的那一軸置中，比容器大的那一軸不能拖出空白。
  // base 可另外指定：重新量測時要用「新的」大小來限制，此時 boxRef 還是舊值
  const clampView = useCallback((zoom, ox, oy, base = boxRef.current) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !base.width || zoom <= MIN_ZOOM) return FIT;
    const cw = wrapper.clientWidth;
    const ch = wrapper.clientHeight;
    const fit = (start, size, span) => (size <= span
      ? (span - size) / 2
      : Math.min(0, Math.max(span - size, start)));
    const left = fit(base.left + ox, base.width * zoom, cw);
    const top = fit(base.top + oy, base.height * zoom, ch);
    return { zoom, ox: left - base.left, oy: top - base.top };
  }, []);

  // 影像用 object-fit: contain 的方式顯示，實際繪製區域不等於容器大小。
  //
  // reset = true（換了影像）時回到符合畫面；否則保留目前的縮放倍率，並讓
  // 原本在畫面中央的那一點維持在中央 —— 放大對準到一半，右側欄位多出一行
  // 提示、版面寬度變了，不該把人踢回 100%。
  const measure = useCallback((reset = false) => {
    const img = imgRef.current;
    const wrapper = wrapperRef.current;
    if (!img || !wrapper || !img.naturalWidth) return;
    const cw = wrapper.clientWidth;
    const ch = wrapper.clientHeight;
    if (!cw || !ch) return;                 // 分頁隱藏中，等顯示後再量
    const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
    const width = img.naturalWidth * scale;
    const drawHeight = img.naturalHeight * scale;
    const next = {
      width, height: drawHeight,
      left: (cw - width) / 2, top: (ch - drawHeight) / 2,
      outerWidth: cw, outerHeight: ch,
    };

    const prev = boxRef.current;
    const current = viewRef.current;
    let nextView = FIT;
    if (!reset && current.zoom > MIN_ZOOM && prev.width) {
      const fx = (prev.outerWidth / 2 - (prev.left + current.ox))
        / (prev.width * current.zoom);
      const fy = (prev.outerHeight / 2 - (prev.top + current.oy))
        / (prev.height * current.zoom);
      const left = cw / 2 - fx * next.width * current.zoom;
      const top = ch / 2 - fy * next.height * current.zoom;
      nextView = clampView(current.zoom, left - next.left, top - next.top, next);
    }

    boxRef.current = next;
    viewRef.current = nextView;
    setBox(next);
    setNatural({ width: img.naturalWidth, height: img.naturalHeight });
    setView(nextView);
  }, [clampView]);

  // 容器大小用 ResizeObserver 追蹤，而不只聽 window resize：Modal 開啟動畫、
  // 捲軸出現或消失、右側欄位內容改變，都會讓影像區變寬變窄卻不觸發 window
  // resize。量測值一過時，點擊位置與標記就會對不上，或標記被畫在可見範圍外
  // —— 放大時點了卻看不到點，就是這個原因。
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const onResize = () => measure(false);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onResize);
      observer.observe(wrapper);
    }
    window.addEventListener("resize", onResize);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [measure]);

  // 換了影像就回到符合畫面
  useEffect(() => { measure(true); }, [measure, src]);

  // 以容器內的 (sx, sy) 為中心縮放：縮放前後游標底下是影像上的同一點
  const zoomAt = useCallback((sx, sy, factor) => {
    const base = boxRef.current;
    const current = viewRef.current;
    if (!base.width) return;
    const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current.zoom * factor));
    if (zoom === current.zoom) return;
    const left = base.left + current.ox;
    const top = base.top + current.oy;
    const fx = (sx - left) / (base.width * current.zoom);
    const fy = (sy - top) / (base.height * current.zoom);
    const nextLeft = sx - fx * base.width * zoom;
    const nextTop = sy - fy * base.height * zoom;
    setView(clampView(zoom, nextLeft - base.left, nextTop - base.top));
  }, [clampView]);

  const zoomCenter = (factor) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    zoomAt(wrapper.clientWidth / 2, wrapper.clientHeight / 2, factor);
  };

  // React 的 onWheel 是 passive listener，preventDefault 無效 —— 滾輪會同時
  // 縮放影像又捲動整個 Modal。所以改掛原生 listener 並指定 passive: false。
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomAt(event.clientX - rect.left, event.clientY - rect.top,
             event.deltaY < 0 ? 1.25 : 0.8);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // 目前實際畫在畫面上的影像位置與大小（已套用縮放與平移）
  const shown = {
    left: box.left + view.ox,
    top: box.top + view.oy,
    width: box.width * view.zoom,
    height: box.height * view.zoom,
  };

  const toImage = useCallback((event) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !shown.width) return null;
    const rect = wrapper.getBoundingClientRect();
    const x = (event.clientX - rect.left - shown.left) / shown.width * natural.width;
    const y = (event.clientY - rect.top - shown.top) / shown.height * natural.height;
    if (x < 0 || y < 0 || x > natural.width || y > natural.height) return null;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  }, [shown.left, shown.top, shown.width, shown.height, natural]);

  const toScreen = useCallback((point) => ({
    x: shown.left + (point.x / natural.width) * shown.width,
    y: shown.top + (point.y / natural.height) * shown.height,
  }), [shown.left, shown.top, shown.width, shown.height, natural]);

  const handleMouseDown = (event) => {
    // 旗標由 mousedown 設定：容器上的 mousedown 清掉它，點標記上的
    // mousedown 設上它（並 stopPropagation，所以兩者不會同時發生）。
    // 每次互動都重新決定，不會有殘留狀態 —— 若改成在 click 裡清掉，「在點上
    // 按下、拖出容器外放開」就不會有 click 事件來清，旗標會卡住並吃掉
    // 下一次正常的點擊。
    suppressClick.current = false;
    // 0 = 左鍵（點擊或拖曳平移）、1 = 滾輪鍵、2 = 右鍵（只平移）
    if (event.button > 2) {
      panRef.current = null;
      return;
    }
    // 滾輪鍵按下時瀏覽器會進入自動捲動模式，必須擋掉才能拿來平移
    if (event.button === 1) event.preventDefault();
    panRef.current = {
      x: event.clientX, y: event.clientY, ox: view.ox, oy: view.oy,
      button: event.button, moved: false,
    };
  };

  const handleClick = (event) => {
    const pan = panRef.current;
    panRef.current = null;
    setPanning(false);
    if (event.button !== 0) return;
    if (pan && pan.moved) return;          // 剛剛是在平移，不是點擊
    if (suppressClick.current) return;
    if (!canAdd || dragging !== null) return;
    const point = toImage(event);
    if (point) onAdd(point);
  };

  const handleMouseMove = (event) => {
    const point = toImage(event);
    setCursor(point);

    if (dragging !== null) {
      if (point && onMove) onMove(dragging, point);
      return;
    }

    const pan = panRef.current;
    // event.buttons 的位元：1 = 左、2 = 右、4 = 滾輪鍵
    const held = { 0: 1, 1: 4, 2: 2 }[pan?.button];
    if (!pan || !(event.buttons & held)) return;
    // 未放大時沒有東西可以平移，左鍵按著晃動仍然算點擊
    if (view.zoom <= MIN_ZOOM) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    const threshold = pan.button === 0 ? LEFT_DRAG_THRESHOLD : PAN_BUTTON_THRESHOLD;
    if (!pan.moved && Math.hypot(dx, dy) < threshold) return;
    pan.moved = true;
    setPanning(true);
    setView(clampView(view.zoom, pan.ox + dx, pan.oy + dy));
  };

  const endPan = () => {
    setPanning(false);
    // 左鍵放開後還會有 click 事件，由 handleClick 收尾；右鍵與滾輪鍵沒有
    // click，要在這裡清掉
    if (panRef.current && panRef.current.button !== 0) panRef.current = null;
  };

  const endDrag = () => setDragging(null);

  const scale = shown.width && natural.width ? shown.width / natural.width : 1;
  // 標記大小隨縮放調整，縮小檢視時才不會整片被標記蓋住；放大後維持上限，
  // 才不會擋住要對準的地物
  const radius = Math.max(4, Math.min(9, 9 * scale * 2));

  // 操作說明只列出當下真的可用的動作
  const legend = [];
  if (canAdd) legend.push("左鍵點擊新增");
  else if (!readOnly && onAdd) legend.push("已達點數上限");
  if (canMove && points.length > 0) legend.push("拖曳調整");
  if (canRemove && points.length > 0) legend.push("右鍵刪除");
  legend.push(view.zoom > MIN_ZOOM ? "滾輪縮放・右鍵拖曳平移" : "滾輪放大");

  const progress = needPoints
    ? `${points.length} / ${needPoints}`
    : `${points.length}`;

  const cursorStyle = panning ? "grabbing"
    : canAdd ? "crosshair"
    : view.zoom > MIN_ZOOM ? "grab" : "default";

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative", height, background: "#111", borderRadius: 4,
        overflow: "hidden", cursor: cursorStyle, userSelect: "none",
      }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={() => { endDrag(); endPan(); }}
      // 右鍵拖曳平移時不能跳出瀏覽器選單。點上的右鍵（刪除）由點自己的
      // onContextMenu 處理並 stopPropagation，不會走到這裡
      onContextMenu={(event) => event.preventDefault()}
      onMouseLeave={() => {
        endDrag();
        setCursor(null);
        setPanning(false);
        panRef.current = null;
      }}
    >
      <img
        ref={imgRef} src={src} alt="" onLoad={() => measure(true)} draggable={false}
        style={{ position: "absolute", left: shown.left, top: shown.top,
                 width: shown.width, height: shown.height, pointerEvents: "none",
                 maxWidth: "none",
                 // 放大後看得到真實像素，比平滑模糊的邊更容易對準
                 imageRendering: view.zoom >= 3 ? "pixelated" : "auto" }}
      />

      {box.width > 0 && (
        // 繪圖層永遠蓋滿整個容器，不用量測值：量測一旦過時，固定寬度的
        // 繪圖層會比容器窄，右側新增的點就被裁掉、完全看不到
        <svg
          width="100%"
          height="100%"
          style={{ position: "absolute", left: 0, top: 0, overflow: "visible",
                   pointerEvents: "none" }}
        >
          {lines.map((line, index) => {
            const a = toScreen(line.from);
            const b = toScreen(line.to);
            return (
              <line key={`line-${index}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={line.color || COLORS.line} strokeWidth={2}
                    strokeDasharray={line.dashed ? "6 4" : undefined} />
            );
          })}

          {grid.map((point, index) => {
            const p = toScreen(point);
            return <circle key={`g-${index}`} cx={p.x} cy={p.y} r={2.5}
                           fill={COLORS.grid} opacity={0.85} />;
          })}

          {points.map((point, index) => {
            const p = toScreen(point);
            const color = point.color || COLORS.grp;
            const interactive = canMove || canRemove;
            return (
              <g key={`p-${index}`}
                 style={{ pointerEvents: interactive ? "auto" : "none",
                          cursor: canMove ? "grab" : "default" }}
                 onMouseDown={(event) => {
                   // 點上按右鍵是要刪除：不能開始拖曳，也不能變成平移
                   if (event.button !== 0) {
                     if (event.button === 2) event.stopPropagation();
                     return;
                   }
                   if (!canMove) return;
                   event.stopPropagation();
                   // 點到既有的點就不該在同一個位置再新增一個，拖曳結束時
                   // 瀏覽器仍會在容器上送出 click
                   suppressClick.current = true;
                   panRef.current = null;
                   setDragging(index);
                 }}
                 onContextMenu={(event) => {
                   event.preventDefault();
                   event.stopPropagation();
                   if (canRemove) onRemove(index);
                 }}>
                {/* 透明的大圓只為了加大可抓取範圍 —— 半徑 4~9px 的圓很難用
                    滑鼠精準點到，尤其是縮小檢視時 */}
                <circle cx={p.x} cy={p.y} r={Math.max(radius + 6, 12)}
                        fill="transparent" />
                <circle cx={p.x} cy={p.y} r={radius} fill="none"
                        stroke={color} strokeWidth={2} />
                <circle cx={p.x} cy={p.y} r={2} fill={color} />
                {point.label !== undefined && (
                  <text x={p.x + radius + 3} y={p.y + 4} fill={color}
                        fontSize={12} fontWeight="bold"
                        style={{ paintOrder: "stroke", stroke: "#000",
                                 strokeWidth: 3 }}>
                    {point.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* 操作說明。pointerEvents: none 很重要 —— 它蓋在影像上方，若會吃掉
          滑鼠事件，上緣那一帶就點不到 */}
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 2,
                    padding: "5px 10px", background: "rgba(0,0,0,0.6)",
                    color: "#f0f0f0", fontSize: 11, lineHeight: 1.6,
                    display: "flex", flexWrap: "wrap", alignItems: "center",
                    gap: 14, pointerEvents: "none" }}>
        {task && <span style={{ fontWeight: 600, color: "#fff" }}>{task}</span>}
        {legend.map((item) => <span key={item}>{item}</span>)}
        <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
          已標 {progress}
        </span>
      </div>

      {/* 還沒有任何點時給一個明顯的提示。第一次用這個介面的人不會知道可以
          直接在影像上點 —— 現地人員大多只用這一次 */}
      {canAdd && points.length === 0 && view.zoom === MIN_ZOOM && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1,
                      display: "flex", alignItems: "center",
                      justifyContent: "center", pointerEvents: "none" }}>
          <span style={{ padding: "6px 14px", borderRadius: 16, fontSize: 13,
                         background: "rgba(0,0,0,0.6)", color: "#fff" }}>
            在影像上點擊以新增第一個點
          </span>
        </div>
      )}

      <div style={{ position: "absolute", left: 8, bottom: 6, fontSize: 11,
                    color: "#ddd", textShadow: "0 0 3px #000",
                    display: "flex", gap: 12, pointerEvents: "none" }}>
        <span>
          {/* 標成 px / py 而不是 x / y：GRP 表格的 X / Y 是現場實測座標
              （公尺），兩者同名時現地人員會把游標的像素值抄進表格 */}
          {cursor ? `像素 px ${cursor.x} · py ${cursor.y}`
                  : `${natural.width}×${natural.height} px`}
        </span>
      </div>

      {/* 縮放按鈕。mousedown / click 都要擋下來，否則按按鈕會在底下新增一點 */}
      {box.width > 0 && (
        <div style={{ position: "absolute", right: 8, bottom: 6, zIndex: 3,
                      display: "flex", gap: 4 }}
             onMouseDown={(event) => event.stopPropagation()}
             onClick={(event) => event.stopPropagation()}
             onMouseMove={(event) => event.stopPropagation()}>
          <button type="button" style={buttonStyle} title="縮小"
                  disabled={view.zoom <= MIN_ZOOM}
                  onClick={() => zoomCenter(1 / 1.5)}>−</button>
          <button type="button" style={buttonStyle} title="符合畫面"
                  onClick={() => setView(FIT)}>
            {Math.round(view.zoom * 100)}%
          </button>
          <button type="button" style={buttonStyle} title="放大"
                  disabled={view.zoom >= MAX_ZOOM}
                  onClick={() => zoomCenter(1.5)}>+</button>
        </div>
      )}
    </div>
  );
}

export { COLORS };